import { useMemo, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Pencil, Boxes } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import {
  useCreateResource,
  useCreateResourceGroup,
  useResourceGroups,
  useResources,
  useUpdateResource,
  useUpdateResourceGroup,
  type ResourceGroupRow,
  type ResourceRow,
} from '@/hooks/useSchedulingResources';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { TimeInput } from '@/components/ui/TimeInput';
import { Checkbox } from '@/components/ui/Checkbox';
import { Modal } from '@/components/ui/Modal';
import { StatusChip } from '@/components/ui/Chip';
import { ErrorAlert } from '@/components/ui/ErrorAlert';
import { TYPE } from '@/lib/typography';
import { cn } from '@/utils/cn';
import {
  SectionCard,
  SettingsChoiceRow,
  SettingsEmpty,
  SettingsLoading,
  SettingsStack,
} from './settingsChrome';

const WEEKDAYS = [
  'monday',
  'tuesday',
  'wednesday',
  'thursday',
  'friday',
  'saturday',
  'sunday',
] as const;
type Weekday = (typeof WEEKDAYS)[number];

interface DayHours {
  enabled: boolean;
  start: string;
  end: string;
}

type AvailabilityShape = Record<Weekday, DayHours>;

const DEFAULT_AVAILABILITY: AvailabilityShape = {
  monday: { enabled: true, start: '08:00', end: '16:00' },
  tuesday: { enabled: true, start: '08:00', end: '16:00' },
  wednesday: { enabled: true, start: '08:00', end: '16:00' },
  thursday: { enabled: true, start: '08:00', end: '16:00' },
  friday: { enabled: true, start: '08:00', end: '16:00' },
  saturday: { enabled: false, start: '09:00', end: '14:00' },
  sunday: { enabled: false, start: '09:00', end: '14:00' },
};

function readAvailability(value: ResourceRow['availability']): AvailabilityShape | null {
  if (!value || typeof value !== 'object') return null;
  const out = { ...DEFAULT_AVAILABILITY };
  let any = false;
  for (const day of WEEKDAYS) {
    const v = value[day];
    if (v && typeof v === 'object') {
      any = true;
      out[day] = {
        enabled: Boolean(v.enabled),
        start: typeof v.start === 'string' ? v.start : DEFAULT_AVAILABILITY[day].start,
        end: typeof v.end === 'string' ? v.end : DEFAULT_AVAILABILITY[day].end,
      };
    }
  }
  return any ? out : null;
}

export function ClinicResources() {
  const { t } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { isClinicAdmin, isPlatformAdmin } = useAuth();
  const canEdit = Boolean(activeClinicId && (isPlatformAdmin || isClinicAdmin(activeClinicId)));

  const {
    data: groups,
    isLoading: loadingGroups,
    error: groupsError,
  } = useResourceGroups(activeClinicId);
  const {
    data: resources,
    isLoading: loadingResources,
    error: resourcesError,
  } = useResources(activeClinicId);
  const createGroup = useCreateResourceGroup(activeClinicId);
  const updateGroup = useUpdateResourceGroup(activeClinicId);
  const createResource = useCreateResource(activeClinicId);
  const updateResource = useUpdateResource(activeClinicId);

  const [groupName, setGroupName] = useState('');
  const [editingGroup, setEditingGroup] = useState<ResourceGroupRow | null>(null);
  const [resourceForm, setResourceForm] = useState<{
    groupId: string;
    resource: ResourceRow | null;
    name: string;
    isActive: boolean;
    customHours: boolean;
    hours: AvailabilityShape;
  } | null>(null);

  const resourcesByGroup = useMemo(() => {
    const map = new Map<string, ResourceRow[]>();
    for (const resource of resources ?? []) {
      const list = map.get(resource.resource_group_id) ?? [];
      list.push(resource);
      map.set(resource.resource_group_id, list);
    }
    return map;
  }, [resources]);

  async function onSaveGroup(e: FormEvent) {
    e.preventDefault();
    const name = (editingGroup?.name ?? groupName).trim();
    if (!name) return;
    try {
      if (editingGroup) {
        await updateGroup.mutateAsync({ id: editingGroup.id, name });
        toast.success(t('settings.resources.group_updated'));
        setEditingGroup(null);
      } else {
        await createGroup.mutateAsync({ name });
        toast.success(t('settings.resources.group_created'));
        setGroupName('');
      }
    } catch (err) {
      console.error('resource group save failed', err);
      toast.error(t('settings.resources.save_error'));
    }
  }

  async function onSaveResource(e: FormEvent) {
    e.preventDefault();
    if (!resourceForm) return;
    const name = resourceForm.name.trim();
    if (!name) return;
    try {
      const availability = resourceForm.customHours ? resourceForm.hours : null;
      if (resourceForm.resource) {
        await updateResource.mutateAsync({
          id: resourceForm.resource.id,
          name,
          is_active: resourceForm.isActive,
          availability,
        });
        toast.success(t('settings.resources.resource_updated'));
      } else {
        await createResource.mutateAsync({
          resource_group_id: resourceForm.groupId,
          name,
          is_active: resourceForm.isActive,
          availability,
        });
        toast.success(t('settings.resources.resource_created'));
      }
      setResourceForm(null);
    } catch (err) {
      console.error('resource save failed', err);
      toast.error(t('settings.resources.save_error'));
    }
  }

  if (loadingGroups || loadingResources) {
    return (
      <SettingsLoading />
    );
  }
  if (groupsError || resourcesError)
    return <ErrorAlert message={t('settings.resources.load_error')} />;

  return (
    <div className="flex min-w-0 flex-col gap-s4">
      <SectionCard
        title={t('settings.resources.title')}
        subtitle={t('settings.resources.subtitle')}
        readOnly={!canEdit}
      >
        {canEdit ? (
          <form onSubmit={onSaveGroup} className="mb-s4 flex min-w-0 flex-col gap-s3 nav:flex-row nav:items-end">
            <div className="min-w-0 flex-1">
              <Input
                label={
                  editingGroup
                    ? t('settings.resources.rename_group')
                    : t('settings.resources.new_group')
                }
                value={editingGroup ? editingGroup.name : groupName}
                onChange={(e) =>
                  editingGroup
                    ? setEditingGroup({ ...editingGroup, name: e.target.value })
                    : setGroupName(e.target.value)
                }
                required
              />
            </div>
            <div className="flex flex-col-reverse gap-s2 nav:flex-row">
              {editingGroup ? (
                <Button type="button" variant="tertiary" onClick={() => setEditingGroup(null)}>
                  {t('common.cancel')}
                </Button>
              ) : null}
              <Button type="submit" disabled={createGroup.isPending || updateGroup.isPending}>
                {editingGroup ? t('common.save') : t('settings.resources.add_group')}
              </Button>
            </div>
          </form>
        ) : null}

        {(groups ?? []).length === 0 ? (
          <SettingsEmpty icon={<Boxes className="h-7 w-7" />} title={t('settings.resources.empty')} />
        ) : (
          <SettingsStack>
            {(groups ?? []).map((group) => {
              const groupResources = resourcesByGroup.get(group.id) ?? [];
              return (
                <div key={group.id} className="rounded-card border border-ds-line p-s4">
                  <div className="mb-s3 flex flex-col gap-s3 nav:flex-row nav:items-center nav:justify-between">
                    <div className="min-w-0">
                      <h3 className={cn(TYPE.section, 'text-ds-text')}>{group.name}</h3>
                      <p className={cn(TYPE.small, 'text-ds-muted')}>
                        {t('settings.resources.resource_count', { count: groupResources.length })}
                      </p>
                    </div>
                    {canEdit ? (
                      <div className="flex flex-wrap items-center gap-s2">
                        <Button
                          type="button"
                          variant="tertiary"
                          size="icon"
                          onClick={() => setEditingGroup(group)}
                          aria-label={t('settings.resources.rename_group')}
                        >
                          <Pencil />
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          onClick={() =>
                            setResourceForm({
                              groupId: group.id,
                              resource: null,
                              name: '',
                              isActive: true,
                              customHours: false,
                              hours: DEFAULT_AVAILABILITY,
                            })
                          }
                        >
                          <Plus className="h-4 w-4" />
                          {t('settings.resources.add_resource')}
                        </Button>
                      </div>
                    ) : null}
                  </div>

                  {groupResources.length === 0 ? (
                    <p className={cn(TYPE.small, 'text-ds-muted')}>
                      {t('settings.resources.no_resources_in_group')}
                    </p>
                  ) : (
                    <ul className="divide-y divide-ds-line">
                      {groupResources.map((resource) => (
                        <li
                          key={resource.id}
                          className="flex flex-col gap-s2 py-s3 nav:flex-row nav:items-center nav:justify-between"
                        >
                          <div className="min-w-0">
                            <p className={cn(TYPE.body, 'text-ds-text')}>{resource.name}</p>
                            <StatusChip
                              tone={resource.is_active ? 'completed' : 'neutral'}
                              className="mt-s1"
                            >
                              {resource.is_active
                                ? t('settings.resources.active')
                                : t('settings.resources.inactive')}
                            </StatusChip>
                          </div>
                          {canEdit ? (
                            <div className="flex items-center gap-s2">
                              <Checkbox
                                checked={resource.is_active}
                                onChange={(e) =>
                                  updateResource.mutate({
                                    id: resource.id,
                                    is_active: e.target.checked,
                                  })
                                }
                                label={t('settings.resources.active')}
                              />
                              <Button
                                type="button"
                                variant="tertiary"
                                size="icon"
                                onClick={() => {
                                  const hours = readAvailability(resource.availability);
                                  setResourceForm({
                                    groupId: group.id,
                                    resource,
                                    name: resource.name,
                                    isActive: resource.is_active,
                                    customHours: hours != null,
                                    hours: hours ?? DEFAULT_AVAILABILITY,
                                  });
                                }}
                                aria-label={t('settings.resources.edit_resource')}
                              >
                                <Pencil />
                              </Button>
                            </div>
                          ) : null}
                        </li>
                      ))}
                    </ul>
                  )}
                </div>
              );
            })}
          </SettingsStack>
        )}
      </SectionCard>

      {resourceForm ? (
        <Modal
          open
          onClose={() => setResourceForm(null)}
          title={
            resourceForm.resource
              ? t('settings.resources.edit_resource')
              : t('settings.resources.add_resource')
          }
          footer={
            <div className="flex w-full flex-col-reverse gap-s2 nav:w-auto nav:flex-row">
              <Button
                type="button"
                variant="tertiary"
                onClick={() => setResourceForm(null)}
                className="w-full nav:w-auto"
              >
                {t('common.cancel')}
              </Button>
              <Button
                type="submit"
                form="resource-form"
                loading={createResource.isPending || updateResource.isPending}
                className="w-full nav:w-auto"
              >
                {t('common.save')}
              </Button>
            </div>
          }
        >
          <form id="resource-form" onSubmit={onSaveResource} className="flex flex-col gap-s4">
            <Input
              label={t('settings.resources.resource_name')}
              value={resourceForm.name}
              onChange={(e) => setResourceForm({ ...resourceForm, name: e.target.value })}
              required
            />
            <Checkbox
              checked={resourceForm.isActive}
              onChange={(e) => setResourceForm({ ...resourceForm, isActive: e.target.checked })}
              label={t('settings.resources.active')}
            />
            <Checkbox
              checked={resourceForm.customHours}
              onChange={(e) =>
                setResourceForm({ ...resourceForm, customHours: e.target.checked })
              }
              label={t('settings.resources.custom_hours')}
            />
            {resourceForm.customHours ? (
              <SettingsStack className="gap-s2">
                {WEEKDAYS.map((day) => {
                  const dh = resourceForm.hours[day];
                  return (
                    <SettingsChoiceRow
                      key={day}
                      className="flex-col items-stretch nav:flex-row nav:items-center"
                    >
                      <Checkbox
                        checked={dh.enabled}
                        onChange={(e) =>
                          setResourceForm({
                            ...resourceForm,
                            hours: {
                              ...resourceForm.hours,
                              [day]: { ...dh, enabled: e.target.checked },
                            },
                          })
                        }
                        label={t(`settings.resources.weekdays.${day}`)}
                      />
                      <div className="flex min-w-0 flex-wrap items-center gap-s2">
                        <TimeInput
                          value={dh.start}
                          disabled={!dh.enabled}
                          onChange={(start) =>
                            setResourceForm({
                              ...resourceForm,
                              hours: {
                                ...resourceForm.hours,
                                [day]: { ...dh, start },
                              },
                            })
                          }
                        />
                        <TimeInput
                          value={dh.end}
                          disabled={!dh.enabled}
                          onChange={(end) =>
                            setResourceForm({
                              ...resourceForm,
                              hours: {
                                ...resourceForm.hours,
                                [day]: { ...dh, end },
                              },
                            })
                          }
                          className="w-32"
                        />
                      </div>
                    </SettingsChoiceRow>
                  );
                })}
              </SettingsStack>
            ) : null}
          </form>
        </Modal>
      ) : null}
    </div>
  );
}
